import React, { useEffect, useRef, useState } from "react";
import { Clock, ChevronDown, Check } from "lucide-react";

/**
 * Styled time picker with hour and minute selection.
 *
 * Props:
 *   value     - "HH:MM" string (e.g. "08:30")
 *   onChange  - callback with new "HH:MM" value
 *   minHour   - minimum hour (default 0, i.e. full 24-hour range)
 *   maxHour   - maximum hour inclusive (default 23)
 */
export default function TimePicker({ value, onChange, minHour = 0, maxHour = 23 }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const [h, m] = (value || "08:00").split(":").map(Number);
  const hour = isNaN(h) ? 8 : h;
  const minute = isNaN(m) ? 0 : m;

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open]);

  const setHour = (newH) => {
    onChange(`${String(newH).padStart(2, "0")}:${String(minute).padStart(2, "0")}`);
  };

  const setMinute = (newM) => {
    onChange(`${String(hour).padStart(2, "0")}:${String(newM).padStart(2, "0")}`);
  };

  const formatLabel = (h, m) => {
    const suffix = h < 12 ? "AM" : "PM";
    const displayH = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${displayH}:${String(m).padStart(2, "0")} ${suffix}`;
  };

  const hours = [];
  for (let i = minHour; i <= maxHour; i++) hours.push(i);

  const minutes = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm font-medium text-gray-900 dark:text-white shadow-sm cursor-pointer hover:border-gray-400 dark:hover:border-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1 transition-colors"
      >
        <Clock className="h-4 w-4 text-gray-500 dark:text-gray-400" />
        {formatLabel(hour, minute)}
        <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute z-50 mt-1 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg">
          <div className="flex">
            {/* Hour column */}
            <div className="border-r border-gray-200 dark:border-gray-700">
              <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                Hour
              </div>
              <ul className="max-h-48 overflow-auto py-1 w-[80px]">
                {hours.map((hr) => {
                  const suffix = hr < 12 ? "AM" : "PM";
                  const displayH = hr === 0 ? 12 : hr > 12 ? hr - 12 : hr;
                  return (
                    <li
                      key={hr}
                      onClick={() => setHour(hr)}
                      className={`flex items-center justify-between px-3 py-1.5 text-sm cursor-pointer transition-colors ${
                        hr === hour
                          ? "bg-indigo-50 dark:bg-indigo-950 text-indigo-700 dark:text-indigo-300 font-medium"
                          : "text-gray-900 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                      }`}
                    >
                      {displayH} {suffix}
                      {hr === hour && <Check className="h-3.5 w-3.5 text-indigo-600 dark:text-indigo-400" />}
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* Minute column */}
            <div>
              <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                Min
              </div>
              <ul className="max-h-48 overflow-auto py-1 w-[70px]">
                {minutes.map((min) => (
                  <li
                    key={min}
                    onClick={() => setMinute(min)}
                    className={`flex items-center justify-between px-3 py-1.5 text-sm cursor-pointer transition-colors ${
                      min === minute
                        ? "bg-indigo-50 dark:bg-indigo-950 text-indigo-700 dark:text-indigo-300 font-medium"
                        : "text-gray-900 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                    }`}
                  >
                    :{String(min).padStart(2, "0")}
                    {min === minute && <Check className="h-3.5 w-3.5 text-indigo-600 dark:text-indigo-400" />}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
